import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X } from 'lucide-react';
import { ReminderConfig } from '../types';
import { getReminderConfig, saveReminderConfig } from '../services/mockService';
import {
  cancelAllDivineNotifications,
  scheduleAllNotifications,
} from '../services/notificationChannels';
import { scheduleNativeDivineReminder, showNativeNotification } from '../services/nativeAlarmBridge';
import { isNativeApp } from '../utils/platform';

const DIVINE_NEXT_FIRE_KEY = 'divine_next_fire_time';

// ─── Web Audio API Chime (fallback for PWA only) ─────────────────────
const playChimeSound = (volume: number) => {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const notes = [523.25, 659.25, 783.99, 1046.50];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t = ctx.currentTime + i * 0.2;
      gain.gain.setValueAtTime(volume * 0.5, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 1.2);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 1.2);
    });
  } catch {}
};

// ─── Play TTS audio from map (PWA only — native app uses notification channel sound) ─
const playMantraAudio = (cfg: ReminderConfig): boolean => {
  if (isNativeApp) return false; // Native notification channel handles sound
  try {
    const audioMap = JSON.parse(localStorage.getItem('mantra_audio_map') || '{}');
    let mantraId = cfg.mantraId || '';
    if (!mantraId || !audioMap[mantraId]) {
      const mantras = JSON.parse(localStorage.getItem('ram_naam_mantras') || '[]');
      const match = mantras.find((m: any) => m.text === cfg.mantraText || m.heading === cfg.mantraText);
      if (match?.id) mantraId = match.id;
    }
    if (!mantraId || !audioMap[mantraId]) {
      mantraId = cfg.mantraText.replace(/\s+/g, '_').substring(0, 30);
    }
    if (mantraId && audioMap[mantraId]) {
      let audioUrl = audioMap[mantraId];
      if (audioUrl.startsWith('/api/')) {
        audioUrl = (process.env.REACT_APP_BACKEND_URL || '') + audioUrl;
      }
      const audio = new Audio(audioUrl);
      audio.volume = cfg.volume;
      audio.play().catch(() => {});
      return true;
    }
  } catch {}
  return false;
};

// ─── Get/Set next fire time ─────────────────────────────────────────
const getNextFireTime = (): number => {
  return parseInt(localStorage.getItem(DIVINE_NEXT_FIRE_KEY) || '0', 10);
};
const setNextFireTime = (t: number) => {
  localStorage.setItem(DIVINE_NEXT_FIRE_KEY, String(t));
};

export const DivineAlarmOverlay: React.FC = () => {
  const [showAlarm, setShowAlarm] = useState(false);
  const [alarmText, setAlarmText] = useState('');
  const [countdown, setCountdown] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fireAlarm = useCallback((cfg: ReminderConfig) => {
    const freshCfg = getReminderConfig();
    if (!freshCfg.enabled) return;

    // Show overlay
    setAlarmText(cfg.mantraText);
    setShowAlarm(true);

    // Vibrate
    if ('vibrate' in navigator) {
      navigator.vibrate([500, 200, 500, 200, 500, 200, 500]);
    }

    // Play audio
    let audioPlayed = playMantraAudio(cfg);
    if (!audioPlayed && cfg.useCustomAudio && cfg.customAudioData) {
      try {
        const a = new Audio(cfg.customAudioData);
        a.volume = cfg.volume;
        a.play().catch(() => {});
        audioPlayed = true;
      } catch {}
    }
    if (!audioPlayed) {
      playChimeSound(cfg.volume);
    }

    // FIX: Show native notification banner when app is minimized
    // Uses NativeAlarmBridge.showNotification() — direct Java call, no Capacitor
    if (isNativeApp()) {
      try {
        showNativeNotification('दिव्य स्मरण', cfg.mantraText);
      } catch {}
    }

    // Schedule next fire time
    const intervalMs = cfg.intervalMinutes * 60 * 1000;
    const nextFire = Date.now() + intervalMs;
    setNextFireTime(nextFire);
    saveReminderConfig({ ...freshCfg, nextFireTime: nextFire });

    // On native: reschedule BOTH native AlarmManager and LocalNotifications
    if (isNativeApp()) {
      // Layer 1: Native AlarmManager (MOST important — survives app kill)
      try {
        scheduleNativeDivineReminder(cfg.intervalMinutes, cfg.mantraText, cfg.mantraSoundName || 'mantra_ram');
      } catch {}

      // Layer 2: Capacitor LocalNotifications (secondary backup)
      if (cfg.mantraSoundName) {
        cancelAllDivineNotifications().then(() => {
          scheduleAllNotifications(cfg.mantraSoundName!, cfg.mantraText, intervalMs, Date.now());
        });
      }
    }
  }, []);

  const dismissAlarm = useCallback(() => {
    setShowAlarm(false);
    if ('vibrate' in navigator) navigator.vibrate(0);
  }, []);

  // No channel-switching needed — always SOUND channel

  // Foreground checker — 1 sec interval
  useEffect(() => {
    timerRef.current = setInterval(() => {
      const cfg = getReminderConfig();
      if (!cfg.enabled) {
        setCountdown('');
        return;
      }

      const now = Date.now();
      let nextFire = cfg.nextFireTime || getNextFireTime();
      const intervalMs = cfg.intervalMinutes * 60 * 1000;

      // First time — set initial fire time
      if (!nextFire || nextFire === 0) {
        nextFire = now + intervalMs;
        setNextFireTime(nextFire);
        saveReminderConfig({ ...cfg, nextFireTime: nextFire });
        return;
      }

      const remaining = Math.max(0, nextFire - now);
      const remainSec = Math.ceil(remaining / 1000);

      // Show countdown
      if (remainSec > 0) {
        const m = Math.floor(remainSec / 60);
        const s = remainSec % 60;
        setCountdown(`${m}:${s.toString().padStart(2, '0')}`);
      }

      // FIRE when time is reached
      if (now >= nextFire) {
        setCountdown('');
        fireAlarm(cfg);
      }
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fireAlarm]);

  return (
    <>
      {countdown && (
        <div
          className="fixed bottom-20 right-3 z-[90] bg-saffron/90 text-white text-xs px-2 py-1 rounded-full shadow font-mono"
          data-testid="divine-countdown"
        >
          {countdown}
        </div>
      )}

      {showAlarm && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md"
          data-testid="divine-alarm-overlay"
        >
          <div className="bg-gradient-to-b from-orange-50 to-amber-100 dark:from-stone-800 dark:to-stone-900 w-[90%] max-w-sm rounded-3xl shadow-2xl border-2 border-saffron overflow-hidden">
            <div className="flex flex-col items-center pt-8 pb-4">
              <div className="w-20 h-20 rounded-full bg-saffron/20 flex items-center justify-center mb-4 animate-pulse">
                <Bell size={40} className="text-saffron" />
              </div>
              <h2 className="text-xl font-serif font-bold text-templeRed dark:text-saffron">
                दिव्य स्मरण
              </h2>
            </div>

            <div className="px-6 py-6 text-center">
              <p className="text-2xl font-bold text-stone-800 dark:text-stone-100 leading-relaxed font-serif">
                {alarmText}
              </p>
            </div>

            <div className="px-6 pb-8">
              <button
                onClick={dismissAlarm}
                className="w-full py-4 bg-templeRed text-white rounded-2xl font-bold text-lg flex items-center justify-center gap-2 shadow-lg active:scale-95"
                data-testid="divine-alarm-dismiss"
              >
                <X size={20} /> जप शुरू करें
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
